import { useCallback, useState } from 'react';
import { useToggler } from '@cbhq/cds-common/hooks/useToggler';
import { useAlert } from '@cbhq/cds-common/overlays/useAlert';
import { Banner } from '@cbhq/cds-web/banner/Banner';
import { Alert } from '@cbhq/cds-web/overlays/Alert';

import { useProject } from ':cloud/contexts/ProjectContext';
import { useSimpleBreakpoints } from ':cloud/hooks/useSimpleBreakpoints';
import { useGetOrganization } from ':cloud/queries/OrganizationQueries/useGetOrganization';
import { useInviteProjectMember } from ':cloud/queries/OrganizationQueries/useInviteProjectMember';
import { useRemoveInvitedProjectMember } from ':cloud/queries/OrganizationQueries/useRemoveInvitedProjectMember';
import { useGetUser } from ':cloud/queries/UserQueries/useGetUser';
import { ProjectUserDetailsNEW, ProjectWithRole } from ':cloud/types/service_proto';
import { AccessTableWrapperNEW } from ':cloud/widgets/access/teams/AccessTableWrapperNEW';
// import { ChangeOwnerWith2FAModal } from ':cloud/widgets/access/teams/ChangeOwnerModalNEW';
import { MobileAccessTableWrapperNEW } from ':cloud/widgets/access/teams/MobileAccessTableWrapperNEW';
import { Layout } from ':cloud/widgets/layout';

import { EditMemberModal } from './EditMemberModalNEW';
import { InviteMemberModal } from './InviteMemberModalNEW';
import { RemoveMemberModal } from './RemoveMemberModalNEW';

function ExchangeUsersBanner() {
  return (
    <Banner
      title="Your Exchange organization has too many users"
      variant="warning"
      startIcon="warning"
    >
      CDP organizations cannot have additional participants in order to be eligible for a paid
      Market Data subscription. Please remove all members or contact support.
    </Banner>
  );
}

export function AccessTableNEW() {
  const { isPhone } = useSimpleBreakpoints();

  const { user, activeOrg } = useGetUser();

  // Remove type cast after project based team management is fully rolled out - useIsProjectTeamsEnabled
  const { selectedProject } = useProject() as {
    selectedProject: ProjectWithRole | null;
  };

  const [selectedUser, setSelectedUser] = useState<ProjectUserDetailsNEW>();
  const { orgHasMultipleUsers } = useGetOrganization(activeOrg?.organizationId);
  const existingUsers = selectedProject?.users?.map((projectUser) => projectUser.email);
  const existingInvites = selectedProject?.invites?.map((projectInvite) => projectInvite.email);
  // const activeUsers = selectedProject?.users || [];

  const [inviteModalVisible, { toggleOn: toggleInviteModalOn, toggleOff: toggleInviteModalOff }] =
    useToggler(false);
  const [editMemberModalVisible, { toggleOn: toggleEditModalOn, toggleOff: toggleEditModalOff }] =
    useToggler(false);
  const [
    removeMemberModalVisible,
    { toggleOn: toggleRemoveModalOn, toggleOff: toggleRemoveModalOff },
  ] = useToggler(false);
  // const [
  //   changeOwnerModalVisible,
  //   { toggleOn: toggleChangeOwnerModalOn, toggleOff: toggleChangeOwnerModalOff },
  // ] = useToggler(false);

  const cancelInvitation = useAlert();
  const resendInvitation = useAlert();

  const reinviteMemberMutation = useInviteProjectMember(
    selectedProject?.id,
    selectedUser?.email,
    true,
  );
  const removeInvitedMemberMutation = useRemoveInvitedProjectMember(
    selectedProject?.id,
    selectedUser?.email,
  );

  /** Reinvite uses Invite member API  */
  const handleReinvitation = useCallback(
    (invitedUser) => {
      setSelectedUser(invitedUser);
      /** keep modal open when users invitation fails due to network error */
      reinviteMemberMutation.mutate(invitedUser?.role, {
        onSuccess: () => {
          resendInvitation.close();
          setSelectedUser(undefined);
        },
      });
    },
    [reinviteMemberMutation, resendInvitation],
  );

  const handleCancelInvitation = useCallback(
    (invitedUser) => {
      setSelectedUser(invitedUser);
      removeInvitedMemberMutation.mutate(undefined, {
        onSuccess: () => {
          cancelInvitation.close();
          setSelectedUser(undefined);
        },
      });
    },
    [cancelInvitation, removeInvitedMemberMutation],
  );

  const handleMemberAction = useCallback(
    (event: string, member: ProjectUserDetailsNEW): void => {
      const handleResendInvitationClose = resendInvitation.close;
      const handleCancelInvitationClose = cancelInvitation.close;
      switch (event) {
        case 'edit-member':
          setSelectedUser(member);
          toggleEditModalOn();
          break;
        case 'remove-member':
          setSelectedUser(member);
          toggleRemoveModalOn();
          break;
        // case 'change-owner':
        //   setSelectedUser(member);
        //   toggleChangeOwnerModalOn();
        //   break;
        case 'resend-invitation':
          setSelectedUser(member);
          resendInvitation.open(
            <Alert
              visible
              title="Are you sure you want to resend this invitation?"
              body=""
              onRequestClose={handleResendInvitationClose}
              preferredActionLabel="Yes, resend"
              onPreferredActionPress={() => handleReinvitation(member)}
              dismissActionLabel="Nevermind"
              onDismissActionPress={handleResendInvitationClose}
            />,
          );
          break;
        case 'cancel-invitation':
          setSelectedUser(member);
          cancelInvitation.open(
            <Alert
              visible
              title="Are you sure you want to cancel this invitation?"
              onRequestClose={handleCancelInvitationClose}
              body=""
              preferredActionLabel="Yes, cancel"
              preferredActionVariant="negative"
              onPreferredActionPress={() => handleCancelInvitation(member)}
              dismissActionLabel="Nevermind"
              onDismissActionPress={handleCancelInvitationClose}
            />,
          );
          break;
        default:
          throw new Error(`Unexpected Action: ${event}`);
      }
    },
    [
      cancelInvitation,
      handleCancelInvitation,
      handleReinvitation,
      resendInvitation,
      // toggleChangeOwnerModalOn,
      toggleEditModalOn,
      toggleRemoveModalOn,
    ],
  );

  return (
    <>
      {inviteModalVisible && (
        <InviteMemberModal
          visible
          existingUsers={existingUsers}
          existingInvites={existingInvites}
          onRequestClose={toggleInviteModalOff}
        />
      )}
      {editMemberModalVisible && selectedUser && (
        <EditMemberModal
          visible
          user={selectedUser}
          onRequestClose={toggleEditModalOff}
          // onChangeOwner={toggleChangeOwnerModalOn}
        />
      )}
      {removeMemberModalVisible && !!selectedUser && (
        <RemoveMemberModal visible user={selectedUser} onRequestClose={toggleRemoveModalOff} />
      )}
      {/* {changeOwnerModalVisible && !!selectedUser && (
        <ChangeOwnerWith2FAModal
          visible
          currentOwner={user}
          user={selectedUser}
          projectId={selectedProject?.id || ''}
          activeUsers={activeUsers}
          onRequestClose={toggleChangeOwnerModalOff}
        />
      )} */}
      {user?.exchangeEnabled && orgHasMultipleUsers && <ExchangeUsersBanner />}
      {isPhone ? (
        <Layout>
          <Layout.MainContainer>
            <MobileAccessTableWrapperNEW
              toggleInviteModalOn={toggleInviteModalOn}
              handleMemberAction={handleMemberAction}
            />
          </Layout.MainContainer>
        </Layout>
      ) : (
        <AccessTableWrapperNEW
          toggleInviteModalOn={toggleInviteModalOn}
          handleMemberAction={handleMemberAction}
        />
      )}
    </>
  );
}
